'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';

import { useAuthStore } from '@/stores/authStore';
import { useAppModeStore } from '@/stores/appModeStore';
import { createConversation, sendInitMessage, waitForInitDone } from '@/lib/api';

/**
 * Replays a stashed PendingIntent once the user has signed in. Runs
 * at the AppLayout level so every page picks up the resume behaviour.
 *
 * - chat_submit / card_prompt: create a fresh SBAA conversation with
 *   the same app_key + depends as the authed flow uses, then set
 *   `pendingInitialPrompt` so ChatView dispatches the prompt into
 *   the new runtime.
 * - voice: just navigate to /voice. The user re-clicks Connect
 *   (per the redesign spec — we don't auto-dial).
 */
export function usePendingIntentResume() {
  const router = useRouter();
  const sessionToken = useAuthStore((s) => s.sessionToken);
  const aurigaUid = useAuthStore((s) => s.aurigaUid);
  const pendingIntent = useAuthStore((s) => s.pendingIntent);
  const setPendingIntent = useAuthStore((s) => s.setPendingIntent);
  const setPendingInitialPrompt = useAuthStore((s) => s.setPendingInitialPrompt);
  const setConversationId = useAuthStore((s) => s.setConversationId);

  const running = useRef(false);

  useEffect(() => {
    if (!sessionToken || !aurigaUid) return;
    if (!pendingIntent) return;
    if (running.current) return;
    running.current = true;

    const intent = pendingIntent;
    const { isAppMode, slug, appConfig } = useAppModeStore.getState();
    const capturedSlug = slug ?? undefined;

    (async () => {
      try {
        if (intent.type === 'voice') {
          router.replace('/voice');
          return;
        }

        // chat_submit / card_prompt: create a fresh conversation and
        // stash the prompt for ChatView to dispatch.
        const depends = isAppMode && appConfig?.source_skill
          ? [appConfig.source_skill] : undefined;
        const newId = await createConversation(
          aurigaUid,
          sessionToken,
          'skill-chat',
          undefined,
          undefined,
          depends,
          isAppMode ? appConfig?.app_key : undefined,
        );
        if (!newId) return;

        setPendingInitialPrompt({ convId: newId, prompt: intent.prompt });
        // Seed a greeting turn if there is no card prompt flow-specific
        // message — matches the AutoCreateConversation init sequence so
        // the server-side state is consistent.
        const requestId = await sendInitMessage(aurigaUid, newId, sessionToken);
        if (requestId) {
          await waitForInitDone(aurigaUid, newId, sessionToken, requestId);
        }
        setConversationId(newId, capturedSlug);
        if (typeof window !== 'undefined' && window.location.pathname !== '/chat') {
          router.replace('/chat');
        }
      } finally {
        setPendingIntent(null);
        running.current = false;
      }
    })();
  }, [
    sessionToken,
    aurigaUid,
    pendingIntent,
    router,
    setPendingIntent,
    setPendingInitialPrompt,
    setConversationId,
  ]);
}
